import { Link } from "react-router-dom";
import {
    BookOpen,
    UserPlus,
    Calendar,
    Activity,
    BarChart3,
    CreditCard,
    Settings as SettingsIcon,
    ArrowLeft,
    LogIn,
    Sparkles,
    Users,
} from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

export default function InstructionsPage() {
    const { user } = useAuth();
    const sections = [
        { id: "primi-passi", label: "Primi passi" },
        { id: "dipendenti", label: "Anagrafica dipendenti" },
        { id: "turni", label: "Turni & calendario" },
        { id: "performance", label: "Performance" },
        { id: "brigata", label: "Brigata & analytics" },
        { id: "abbonamento", label: "Abbonamento & fatturazione" },
        { id: "account", label: "Account & sicurezza" },
        { id: "faq", label: "Domande frequenti" },
    ];

    return (
        <div className="min-h-screen bg-sf-bg" data-testid="instructions-page">
            {/* Top nav */}
            <header className="glass-nav sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
                    <Link to={user ? "/app" : "/"}>
                        <Logo className="h-28 w-auto" />
                    </Link>
                    <div className="flex items-center gap-2">
                        <Link to={user ? "/app" : "/"}>
                            <Button variant="ghost" data-testid="instructions-back">
                                <ArrowLeft size={16} className="mr-2" />
                                {user ? "Dashboard" : "Home"}
                            </Button>
                        </Link>
                        {!user && (
                            <Link to="/register">
                                <Button
                                    className="bg-navy text-white"
                                    data-testid="instructions-register"
                                >
                                    Inizia gratis
                                </Button>
                            </Link>
                        )}
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="hero-mesh">
                <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 md:py-24">
                    <div className="label-eyebrow mb-4">Guida</div>
                    <h1 className="font-display text-5xl md:text-6xl text-navy leading-tight mb-4">
                        Come usare
                        <span className="italic text-amber-brand">
                            {" "}
                            Staff Flow 360
                        </span>
                    </h1>
                    <p className="text-lg text-sf-secondary max-w-2xl leading-relaxed">
                        Una guida passo-passo per partire in 5 minuti: carica
                        la brigata, pianifica turni, traccia performance e
                        leggi i KPI. Tutto in italiano, pensato per il ritmo
                        della sala.
                    </p>
                </div>
            </section>

            <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10">
                {/* Sidebar TOC */}
                <aside className="lg:col-span-3">
                    <nav
                        className="sticky top-24 bg-white border border-sf rounded-xl p-4"
                        data-testid="instructions-toc"
                    >
                        <div className="label-eyebrow mb-3">Indice</div>
                        <ul className="space-y-1.5 text-sm">
                            {sections.map((s) => (
                                <li key={s.id}>
                                    <a
                                        href={`#${s.id}`}
                                        className="block px-2 py-1.5 rounded text-sf-secondary hover:bg-[#f0efeb] hover:text-navy"
                                        data-testid={`toc-${s.id}`}
                                    >
                                        {s.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </aside>

                {/* Content */}
                <article className="lg:col-span-9 space-y-16 text-sf-secondary leading-relaxed">
                    <Section
                        id="primi-passi"
                        icon={LogIn}
                        title="1. Primi passi"
                        subtitle="Crea un account e accedi alla dashboard."
                    >
                        <Step n={1} title="Crea il tuo account">
                            Vai su{" "}
                            <Link
                                to="/register"
                                className="text-navy font-semibold underline"
                            >
                                Registrati
                            </Link>{" "}
                            e inserisci nome, locale, email e password. Il
                            piano di partenza è il trial gratuito (max 3
                            dipendenti).
                        </Step>
                        <Step n={2} title="Accedi alla dashboard">
                            Dopo la registrazione vieni reindirizzato a{" "}
                            <code className="font-mono text-xs bg-[#f0efeb] px-1.5 py-0.5 rounded">
                                /app
                            </code>
                            : qui trovi i KPI principali della settimana e le
                            azioni rapide.
                        </Step>
                        <Step n={3} title="Naviga dalla sidebar">
                            La sidebar a sinistra contiene tutte le sezioni:
                            Dipendenti, Turni, Performance, Brigata,
                            Impostazioni.
                        </Step>
                        <Tip>
                            Su mobile usa l'icona menu in alto a destra per
                            aprire la sidebar.
                        </Tip>
                    </Section>

                    <Section
                        id="dipendenti"
                        icon={UserPlus}
                        title="2. Anagrafica dipendenti"
                        subtitle="Carica camerieri, chef, barman e tutto lo staff."
                    >
                        <Step n={1} title="Apri la sezione Dipendenti">
                            Dalla sidebar clicca{" "}
                            <strong className="text-navy">Dipendenti</strong>.
                            Vedrai la tabella con tutti i membri della brigata.
                        </Step>
                        <Step n={2} title="Aggiungi un dipendente">
                            Clicca il pulsante{" "}
                            <strong className="text-navy">+ Aggiungi</strong> in
                            alto a destra. Compila il modulo:
                            <ul className="list-disc pl-6 mt-2 space-y-1">
                                <li>
                                    <strong>Nome</strong>: nome completo
                                </li>
                                <li>
                                    <strong>Ruolo</strong>: Cameriere, Maître,
                                    Sommelier, Barman, Chef, Sous-chef,
                                    Lavapiatti, Hostess, Runner
                                </li>
                                <li>
                                    <strong>Tariffa oraria</strong>: in euro
                                </li>
                                <li>
                                    <strong>Email</strong> e{" "}
                                    <strong>telefono</strong> (opzionali)
                                </li>
                                <li>
                                    <strong>Stato</strong>: Attivo / Inattivo
                                </li>
                            </ul>
                        </Step>
                        <Step n={3} title="Modifica o elimina">
                            Dalla tabella, usa le icone matita e cestino sulla
                            riga del dipendente. Eliminando un dipendente
                            vengono rimossi anche i suoi turni e record di
                            performance.
                        </Step>
                        <Warning>
                            <strong>Attenzione ai limiti del piano:</strong>{" "}
                            Free trial → max 3 attivi · Base €9 → max 10 attivi
                            · Pro €19 → illimitati. Al raggiungimento del limite
                            il pulsante "+ Aggiungi" è disabilitato.
                        </Warning>
                    </Section>

                    <Section
                        id="turni"
                        icon={Calendar}
                        title="3. Turni & calendario"
                        subtitle="Pianifica la settimana in pochi click."
                    >
                        <Step n={1} title="Apri la sezione Turni">
                            Sidebar → <strong className="text-navy">Turni</strong>.
                            Di default vedi la vista{" "}
                            <strong>Calendario</strong> settimanale
                            (Lunedì–Domenica).
                        </Step>
                        <Step n={2} title="Naviga tra le settimane">
                            Usa le frecce ← → sopra il calendario. La data
                            corrente è evidenziata.
                        </Step>
                        <Step n={3} title="Crea un turno">
                            Due modi:
                            <ul className="list-disc pl-6 mt-2 space-y-1">
                                <li>
                                    Click sull'icona <strong>+</strong> in alto
                                    a destra → modal completo
                                </li>
                                <li>
                                    Click sull'icona <strong>+</strong> dentro
                                    una colonna giorno → pre-compila la data
                                </li>
                            </ul>
                            Indica dipendente, data, orario inizio/fine, ruolo
                            di servizio e postazione (Sala, Bar, Cucina…).
                        </Step>
                        <Step n={4} title="Cambia vista">
                            In alto, toggle tra{" "}
                            <strong className="text-navy">Calendario</strong> e{" "}
                            <strong className="text-navy">Tabella</strong>. La
                            tabella mostra tutti i turni della settimana in
                            elenco.
                        </Step>
                        <Step n={5} title="Elimina un turno">
                            Click sull'icona cestino sul card del turno (vista
                            calendario) o sulla riga (vista tabella).
                        </Step>
                        <Tip>
                            Ogni dipendente ha un colore consistente nel
                            calendario, così riconosci la copertura della
                            brigata a colpo d'occhio.
                        </Tip>
                    </Section>

                    <Section
                        id="performance"
                        icon={Activity}
                        title="4. Performance"
                        subtitle="Coperti, scontrino medio, mance, rating, puntualità."
                    >
                        <Step n={1} title="Registra una performance">
                            Sidebar →{" "}
                            <strong className="text-navy">Performance</strong> →{" "}
                            <strong>+ Registra</strong>. Compila per ogni
                            servizio:
                            <ul className="list-disc pl-6 mt-2 space-y-1">
                                <li>
                                    <strong>Dipendente</strong> e{" "}
                                    <strong>data</strong>
                                </li>
                                <li>
                                    <strong>Ore lavorate</strong> (con
                                    decimali, es. 6.5)
                                </li>
                                <li>
                                    <strong>Coperti</strong> serviti
                                </li>
                                <li>
                                    <strong>Incasso</strong> totale generato
                                    (€)
                                </li>
                                <li>
                                    <strong>Mance</strong> raccolte (€)
                                </li>
                                <li>
                                    <strong>Rating</strong> 0–5 (qualità
                                    servizio)
                                </li>
                                <li>
                                    Toggle <strong>Puntuale</strong> per
                                    tracciare la presenza
                                </li>
                            </ul>
                        </Step>
                        <Step n={2} title="Scontrino medio automatico">
                            È calcolato come{" "}
                            <code className="font-mono text-xs bg-[#f0efeb] px-1.5 py-0.5 rounded">
                                incasso ÷ coperti
                            </code>
                            . Lo vedi nella tabella sotto i grafici.
                        </Step>
                        <Step n={3} title="Filtra per dipendente">
                            Usa il selettore in alto per vedere solo i record
                            di una persona.
                        </Step>
                        <Step n={4} title="Leggi i grafici">
                            <ul className="list-disc pl-6 mt-2 space-y-1">
                                <li>
                                    <strong>Andamento incassi & coperti</strong>:
                                    linea temporale per capire trend
                                </li>
                                <li>
                                    <strong>Rating medio per dipendente</strong>:
                                    barra per riconoscere chi tira di più
                                </li>
                            </ul>
                        </Step>
                        <Tip>
                            Registra una performance{" "}
                            <strong>ogni fine servizio</strong> per avere dati
                            puliti e KPI accurati nella dashboard.
                        </Tip>
                    </Section>

                    <Section
                        id="brigata"
                        icon={BarChart3}
                        title="5. Brigata & analytics"
                        subtitle="Ranking, copertura turni, incassi per ruolo."
                    >
                        <p>
                            La sezione{" "}
                            <strong className="text-navy">Brigata</strong>{" "}
                            aggrega tutti i dati di performance e ti dà tre
                            viste:
                        </p>
                        <ul className="list-disc pl-6 space-y-2 mt-3">
                            <li>
                                <strong className="text-navy">
                                    Copertura turni per giorno
                                </strong>
                                : bar chart con la distribuzione settimanale —
                                identifica i giorni scoperti.
                            </li>
                            <li>
                                <strong className="text-navy">
                                    Incasso per ruolo
                                </strong>
                                : pie chart con la quota di fatturato generata
                                da ogni ruolo (Sala, Cucina, Bar…).
                            </li>
                            <li>
                                <strong className="text-navy">
                                    Ranking brigata
                                </strong>
                                : tabella ordinata per incasso generato, con
                                ore, coperti, mance, rating, puntualità per ogni
                                persona.
                            </li>
                        </ul>
                        <Tip>
                            Usa il ranking a fine mese per riconoscere i top
                            performer (e magari premiarli con un bonus mance).
                        </Tip>
                    </Section>

                    <Section
                        id="abbonamento"
                        icon={CreditCard}
                        title="6. Abbonamento & fatturazione"
                        subtitle="Trial → Base €9 → Pro €19. Pagamento Stripe sicuro."
                    >
                        <Step n={1} title="Confronta i piani">
                            Vai su{" "}
                            <Link
                                to="/pricing"
                                className="text-navy font-semibold underline"
                            >
                                Prezzi
                            </Link>{" "}
                            o sezione{" "}
                            <strong className="text-navy">
                                Impostazioni → Abbonamento
                            </strong>
                            .
                        </Step>
                        <Step n={2} title="Sottoscrivi un piano">
                            Click su{" "}
                            <strong className="text-navy">
                                Sottoscrivi €9/mese
                            </strong>{" "}
                            (Base) o{" "}
                            <strong className="text-navy">
                                Sottoscrivi €19/mese
                            </strong>{" "}
                            (Pro). Verrai reindirizzato al checkout Stripe.
                        </Step>
                        <Step n={3} title="Conferma pagamento">
                            Dopo il pagamento Stripe ti riporta su{" "}
                            <code className="font-mono text-xs bg-[#f0efeb] px-1.5 py-0.5 rounded">
                                /billing/success
                            </code>
                            . Quando vedi "Pagamento confermato" il piano è
                            attivo.
                        </Step>
                        <Step n={4} title="Cosa cambia">
                            <ul className="list-disc pl-6 mt-2 space-y-1">
                                <li>
                                    <strong>Base</strong>: 10 dipendenti
                                    attivi, calendario, anagrafica, report
                                    base, email support
                                </li>
                                <li>
                                    <strong>Pro</strong>: dipendenti
                                    illimitati, performance avanzate, brigata
                                    analytics, multi-locale, support
                                    prioritario
                                </li>
                            </ul>
                        </Step>
                        <Tip>
                            Puoi <strong>cambiare piano in qualsiasi
                            momento</strong> dalla pagina Prezzi. Stripe gestisce
                            il prorata automaticamente.
                        </Tip>
                    </Section>

                    <Section
                        id="account"
                        icon={SettingsIcon}
                        title="7. Account & sicurezza"
                        subtitle="Profilo, dati locale, logout."
                    >
                        <Step n={1} title="Visualizza il profilo">
                            Sidebar →{" "}
                            <strong className="text-navy">Impostazioni</strong>.
                            Trovi nome, email, locale, ruolo e stato
                            abbonamento.
                        </Step>
                        <Step n={2} title="Esci dall'app">
                            In fondo alla sidebar, pulsante{" "}
                            <strong className="text-navy">Esci</strong>. La
                            sessione viene chiusa e i cookie cancellati.
                        </Step>
                        <Tip>
                            Le password sono salvate con bcrypt; le sessioni
                            usano cookie httpOnly + SameSite=None su HTTPS.
                            Nessun token transita in localStorage.
                        </Tip>
                    </Section>

                    <Section
                        id="faq"
                        icon={BookOpen}
                        title="8. Domande frequenti"
                        subtitle="Risposte rapide alle situazioni più comuni."
                    >
                        <Faq q="Posso provare Staff Flow 360 senza pagare?">
                            Sì. Registrandoti hai un trial gratuito illimitato
                            nel tempo, con max 3 dipendenti attivi. Tutto il
                            resto è accessibile.
                        </Faq>
                        <Faq q="Cosa succede se supero il limite del piano?">
                            Il pulsante "+ Aggiungi" viene disabilitato e
                            vedrai un banner con il link per fare upgrade. I
                            dati già caricati restano sempre accessibili.
                        </Faq>
                        <Faq q="Posso cancellare l'abbonamento?">
                            Sì, in qualsiasi momento. Vai sul tuo portale
                            Stripe (link da Impostazioni → Abbonamento, in
                            arrivo). Manteniamo i tuoi dati attivi anche dopo
                            la cancellazione.
                        </Faq>
                        <Faq q="I dati dei miei camerieri sono al sicuro?">
                            Sì. I dati sono salvati su MongoDB con accesso
                            ristretto al tuo account. Nessuno vede l'anagrafica
                            di altri locali. Conformità GDPR.
                        </Faq>
                        <Faq q="Posso importare/esportare dati?">
                            L'export PDF/CSV è incluso nel piano Pro (in
                            roadmap). L'import massivo è previsto dopo il
                            feedback iniziale.
                        </Faq>
                        <Faq q="Funziona su mobile?">
                            Sì, l'interfaccia è completamente responsive.
                            Stiamo lavorando a una PWA installabile.
                        </Faq>
                        <Faq q="Posso dare accesso ai miei camerieri?">
                            La funzione "Login cameriere" (vista personale dei
                            propri turni) è in roadmap P1. Per ora l'accesso è
                            solo manager.
                        </Faq>
                    </Section>

                    <div
                        className="bg-navy text-white rounded-2xl p-8 md:p-10 mt-12"
                        data-testid="instructions-cta-bottom"
                    >
                        <div className="grid md:grid-cols-2 gap-6 items-center">
                            <div>
                                <div className="label-eyebrow text-amber-brand mb-2">
                                    Pronto a partire?
                                </div>
                                <div className="font-display text-3xl mb-2">
                                    {user
                                        ? "Apri la tua dashboard"
                                        : "Inizia gratis in 60 secondi"}
                                </div>
                                <div className="text-white/70 text-sm">
                                    Nessuna carta richiesta. Cancella quando
                                    vuoi.
                                </div>
                            </div>
                            <div className="flex md:justify-end gap-3">
                                {user ? (
                                    <Link to="/app">
                                        <Button className="bg-amber-brand text-navy font-bold lift">
                                            Vai alla dashboard
                                        </Button>
                                    </Link>
                                ) : (
                                    <>
                                        <Link to="/register">
                                            <Button className="bg-amber-brand text-navy font-bold lift">
                                                <Sparkles
                                                    size={16}
                                                    className="mr-2"
                                                />
                                                Registrati
                                            </Button>
                                        </Link>
                                        <Link to="/pricing">
                                            <Button
                                                variant="outline"
                                                className="border-white text-white hover:bg-white/10"
                                            >
                                                Vedi i piani
                                            </Button>
                                        </Link>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </article>
            </div>

            <footer className="border-t border-sf bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
                    <Logo className="h-28 w-auto" />
                    <div className="text-xs text-sf-muted">
                        Staff Flow 360 è un prodotto di Makai · P.IVA 09364410969
                    </div>
                </div>
            </footer>
        </div>
    );
}

function Section({ id, icon: Icon, title, subtitle, children }) {
    return (
        <section id={id} className="scroll-mt-24" data-testid={`section-${id}`}>
            <div className="flex items-start gap-4 mb-6">
                <div className="bg-amber-brand text-navy w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon size={22} />
                </div>
                <div>
                    <h2 className="font-display text-3xl md:text-4xl text-navy leading-tight">
                        {title}
                    </h2>
                    {subtitle && (
                        <div className="text-sf-secondary mt-1">{subtitle}</div>
                    )}
                </div>
            </div>
            <div className="space-y-5 pl-0 md:pl-16">{children}</div>
        </section>
    );
}

function Step({ n, title, children }) {
    return (
        <div className="flex gap-4">
            <div className="font-display text-2xl text-amber-brand font-semibold flex-shrink-0 w-8">
                {n}.
            </div>
            <div>
                <div className="font-semibold text-navy mb-1">{title}</div>
                <div>{children}</div>
            </div>
        </div>
    );
}

function Tip({ children }) {
    return (
        <div className="bg-amber-brand/15 border-l-4 border-amber-brand p-4 rounded text-navy text-sm">
            <strong>💡 Suggerimento:</strong> {children}
        </div>
    );
}

function Warning({ children }) {
    return (
        <div className="bg-[#d90429]/8 border-l-4 border-[#d90429] p-4 rounded text-sm">
            {children}
        </div>
    );
}

function Faq({ q, children }) {
    return (
        <details
            className="bg-white border border-sf rounded-lg p-4 group"
            data-testid={`faq-${q.toLowerCase().replace(/\s+/g, "-").slice(0, 30)}`}
        >
            <summary className="font-semibold text-navy cursor-pointer flex items-center justify-between">
                {q}
                <span className="text-amber-brand text-xl group-open:rotate-45 transition-transform">
                    +
                </span>
            </summary>
            <div className="mt-3 text-sf-secondary text-sm leading-relaxed">
                {children}
            </div>
        </details>
    );
}
